/**
 * PendingActionsDemo
 * --------------------------------------------------------------
 * Interactive, client-only walkthrough of the "פעולות ממתינות" flow.
 *
 * Renders a SAMPLE meeting that looks exactly like a real pending item
 * (same `cs-pa-*` markup and classes as CollapsibleSections) and walks the
 * therapist through the scripted happy path:
 *
 *   approval ✓  →  summary ✓ (peek at the report)  →  payment ✗  →  reminder ✓
 *
 * Every off-script click shows what would happen for a real meeting and keeps
 * the user on the path. Nothing here touches Firestore, RTK Query, tasks,
 * transactions, or activity logs — all state is local and thrown away.
 *
 * Coaching copy is delivered by Lenny (the in-app assistant) for continuity
 * with the support channels introduced in the welcome onboarding.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Check,
  X,
  Clock,
  NotePencil,
  CurrencyCircleDollar,
  CaretDown,
  CheckCircle,
  Wine,
  ArrowCounterClockwise,
  Info,
} from "@phosphor-icons/react";
import lennyIcon from "../../../assets/lenny-icon-512.jpg";
import useGenderText from "../catchup/useGenderText";
import ContentLoader from "../../shared/ContentLoader";
import "./PendingActionsDemo.css";

const PHASE = {
  INTRO: "intro",
  APPROVAL: "approval",
  SUMMARY: "summary",
  SUMMARY_MOCK: "summary_mock",
  PAYMENT: "payment",
  PAYMENT_TASK: "payment_task",
  DONE: "done",
};

const STAGES = [
  { key: "approval", title: "אישור פגישה", shortTitle: "אישור", Icon: Clock },
  { key: "summary", title: "סיכום טיפול", shortTitle: "סיכום", Icon: NotePencil },
  { key: "payment", title: "תשלום", shortTitle: "תשלום", Icon: CurrencyCircleDollar },
];

const SAMPLE_AMOUNT = 350;
const SIMULATED_SAVE_MS = 650;

// Yesterday at 16:00 local — reads as a meeting that "just ended".
const buildSampleAppointment = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  d.setHours(16, 0, 0, 0);
  return {
    id: "__demo__",
    patientName: "מטופל דוגמא",
    startTime: d.toISOString(),
    paymentAmount: SAMPLE_AMOUNT,
  };
};

const formatTime = (date) =>
  new Date(date).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });

const formatDate = (date) =>
  new Date(date).toLocaleDateString("he-IL", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

const stageDoneByPhase = (phase) => {
  const order = [
    PHASE.INTRO,
    PHASE.APPROVAL,
    PHASE.SUMMARY,
    PHASE.SUMMARY_MOCK,
    PHASE.PAYMENT,
    PHASE.PAYMENT_TASK,
    PHASE.DONE,
  ];
  const idx = order.indexOf(phase);
  return {
    approval: idx > order.indexOf(PHASE.APPROVAL),
    summary: idx > order.indexOf(PHASE.SUMMARY_MOCK),
    payment: idx > order.indexOf(PHASE.PAYMENT_TASK),
  };
};

export default function PendingActionsDemo({ onClose }) {
  const g = useGenderText();
  const [apt] = useState(buildSampleAppointment);
  const [phase, setPhase] = useState(PHASE.INTRO);
  const [saving, setSaving] = useState(false);
  const [hint, setHint] = useState(null);
  const [amount, setAmount] = useState(String(SAMPLE_AMOUNT));
  const [runKey, setRunKey] = useState(0);
  const timerRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Keep the coach bubble in view as the flow progresses (mobile especially).
  useEffect(() => {
    if (phase === PHASE.INTRO) return;
    rootRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [phase]);

  const advance = useCallback((next) => {
    setHint(null);
    setSaving(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setSaving(false);
      setPhase(next);
    }, SIMULATED_SAVE_MS);
  }, []);

  const restart = () => {
    clearTimeout(timerRef.current);
    setSaving(false);
    setHint(null);
    setAmount(String(SAMPLE_AMOUNT));
    setPhase(PHASE.INTRO);
    setRunKey((k) => k + 1);
  };

  const done = stageDoneByPhase(phase);
  const isExpanded = phase !== PHASE.INTRO;
  const pendingCount = Object.values(done).filter((v) => !v).length;

  // ---------------------------------------------------------------
  // Coach copy (Lenny)
  // ---------------------------------------------------------------
  const coach = (() => {
    switch (phase) {
      case PHASE.INTRO:
        return {
          title: "ככה שום דבר לא נשכח",
          text: (
            <>
              כל פגישה שהסתיימה מגיעה לכאן ומחכה לשלושה עדכונים קצרים:{" "}
              <strong>התקיימה? סוכמה? שולמה?</strong> ככה עובדים מאות מטפלים
              ומטפלות במגוון תחומים — וככה הבלגן מסתדר.
              <br />
              זו <strong>פגישה לדוגמה</strong>. שום דבר כאן לא נשמר ולא נוגע
              בנתונים האמיתיים.{" "}
              {g("לחץ", "לחצי")} על השורה כדי לפתוח אותה.
            </>
          ),
          action: { label: "פתיחת הפגישה", onClick: () => setPhase(PHASE.APPROVAL) },
        };
      case PHASE.APPROVAL:
        return {
          title: "שלב 1 — האם הפגישה התקיימה?",
          text: (
            <>
              המערכת שואלת רק מה שצריך. {g("לחץ", "לחצי")} על{" "}
              <span className="pad-key pad-key-yes"><Check size={11} weight="bold" /></span>{" "}
              כדי לאשר שהפגישה אכן התקיימה.
            </>
          ),
        };
      case PHASE.SUMMARY:
        return {
          title: "שלב 2 — סיכום טיפול",
          text: (
            <>
              {g("לחץ", "לחצי")} על{" "}
              <span className="pad-key pad-key-yes"><Check size={11} weight="bold" /></span>{" "}
              כדי לראות לאן זה מוביל בפגישה אמיתית.
            </>
          ),
        };
      case PHASE.SUMMARY_MOCK:
        return {
          title: "בפגישה אמיתית — עוברים לדף תיעוד הטיפול",
          text: (
            <>
              לחיצה על ✓ בפגישה אמיתית מעבירה אותך ישירות לדף{" "}
              <strong>תיעוד הטיפול</strong> של המטופל/ת, כשהפגישה כבר מקושרת.
              בהדגמה נשארים כאן — {g("קרא", "קראי")} מה יש בדף הזה{" "}
              {g("ולחץ", "ולחצי")} <strong>הבנתי, ממשיכים</strong>.
            </>
          ),
        };
      case PHASE.PAYMENT:
        return {
          title: "שלב 3 — תשלום",
          text: (
            <>
              הסכום כבר ממולא לפי התעריף של המטופל/ת. הפעם נבחר{" "}
              <span className="pad-key pad-key-no"><X size={11} weight="bold" /></span>{" "}
              (לא שולם) כדי לראות מה קורה כשעוד לא שילמו.
            </>
          ),
        };
      case PHASE.PAYMENT_TASK:
        return {
          title: "לא שולם? לא נשכח.",
          text: (
            <>
              המערכת מציעה <strong>תזכורת גבייה</strong>. {g("לחץ", "לחצי")}{" "}
              <span className="pad-key pad-key-yes"><Check size={11} weight="bold" /></span>{" "}
              — בפגישה אמיתית נוצרת משימה ב"ארגז הכלים" עם תאריך יעד בעוד 6
              ימים. כאן זו רק הדגמה.
            </>
          ),
        };
      default:
        return null;
    }
  })();

  // ---------------------------------------------------------------
  // Off-script explanations (teach the branch, keep the path)
  // ---------------------------------------------------------------
  const offScript = {
    approvalNo:
      "בפגישה אמיתית: הפגישה תסומן כבוטלה, ותוכלו לתעד דמי ביטול. להדגמה — לחצו ✓.",
    summaryNo:
      "בפגישה אמיתית: תוצע תזכורת לכתוב את הסיכום מאוחר יותר. להדגמה — לחצו ✓.",
    paymentYes:
      "בפגישה אמיתית: בוחרים תאריך ואמצעי תשלום, והפגישה עוברת ל'שולם' בעמוד הפיננסים. להדגמה — לחצו ✗.",
    taskNo:
      "בפגישה אמיתית: החוב יישאר פתוח בעמוד הפיננסים, בלי תזכורת. להדגמה — לחצו ✓.",
  };

  // ---------------------------------------------------------------
  // Stage bodies
  // ---------------------------------------------------------------
  const renderSaving = () => (
    <ContentLoader size="sm" compact message="מעדכן..." />
  );

  const renderHint = () =>
    hint ? (
      <div className="pad-hint" role="status">
        <Info size={14} weight="fill" />
        <span>{hint}</span>
      </div>
    ) : null;

  const renderStageBody = (stageKey) => {
    if (saving) return renderSaving();

    if (stageKey === "approval" && phase === PHASE.APPROVAL) {
      return (
        <div className="cs-pa-step-content">
          <p className="cs-pa-question">הפגישה התרחשה?</p>
          <div className="cs-pa-action-btns">
            <button
              type="button"
              className="cs-pa-btn cs-pa-btn-confirm pad-pulse"
              onClick={() => advance(PHASE.SUMMARY)}
              aria-label="כן, התקיימה"
            >
              <Check size={16} weight="bold" />
            </button>
            <button
              type="button"
              className="cs-pa-btn cs-pa-btn-cancel"
              onClick={() => setHint(offScript.approvalNo)}
              aria-label="לא התקיימה"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
          {renderHint()}
        </div>
      );
    }

    if (stageKey === "summary" && phase === PHASE.SUMMARY) {
      return (
        <div className="cs-pa-step-content">
          <p className="cs-pa-question">להוסיף סיכום פגישה?</p>
          <div className="cs-pa-action-btns">
            <button
              type="button"
              className="cs-pa-btn cs-pa-btn-confirm pad-pulse"
              onClick={() => advance(PHASE.SUMMARY_MOCK)}
              aria-label="כן, לכתוב סיכום"
            >
              <Check size={16} weight="bold" />
            </button>
            <button
              type="button"
              className="cs-pa-btn cs-pa-btn-cancel"
              onClick={() => setHint(offScript.summaryNo)}
              aria-label="לא עכשיו"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
          {renderHint()}
        </div>
      );
    }

    if (stageKey === "summary" && phase === PHASE.SUMMARY_MOCK) {
      return (
        <div className="cs-pa-step-content pad-explain-wrap">
          <div className="pad-explain">
            <div className="pad-explain-head">
              <NotePencil size={14} weight="fill" />
              <span>דף תיעוד הטיפול — מה יש בו</span>
            </div>
            <p className="pad-explain-intro">
              כאן מתועד מה שקרה בטיפול: הסיכום, הממצאים ותוכנית ההמשך — והכל
              נשמר בתיק המטופל, מקושר לפגישה הזו.
            </p>
            <ul className="pad-explain-list">
              <li><strong>בחירת תבנית</strong> — תבנית סיכום מוכנה שמתאימה לסוג הטיפול שלך, ואפשר גם ליצור תבניות מותאמות אישית משלך</li>
              <li><strong>פרטי הטיפול</strong> — סוג טיפול, שיטה ונושאים שעלו</li>
              <li><strong>תוכן חופשי</strong> — תיאור מפורט של מה שקרה במפגש</li>
              <li><strong>העשרת טקסט AI</strong> — לוקח את מה שכתבת ומנסח אותו לסיכום מקצועי ומסודר</li>
              <li><strong>התייעצות עם AI</strong> — שואלים שאלה על המקרה ומקבלים כיוון מחשבה, בלי לצאת מהדף</li>
              <li><strong>קבצים</strong> — צירוף תמונות, מסמכים וציורים</li>
            </ul>
            <p className="pad-explain-note">
              אחרי שמירה התיעוד ננעל, אבל תמיד אפשר לערוך.
              <br />
              רוצים להוסיף הערות תוך כדי הפגישה? פותחים את המערכת בזמן המפגש עם
              המטופל/ת, ומופיעה אפשרות להיכנס ל"מפגש חי" — שם רושמים הערות ונקודות
              במהלך הטיפול. ההערות נשמרות בתיעוד הטיפול, וכשמסכמים את הפגישה רואים
              אותן מולכם.
            </p>
          </div>
          <div className="cs-pa-text-btns">
            <button
              type="button"
              className="cs-pa-text-btn cs-pa-text-btn-primary pad-pulse"
              onClick={() => advance(PHASE.PAYMENT)}
            >
              הבנתי, ממשיכים
            </button>
          </div>
        </div>
      );
    }

    if (stageKey === "payment" && phase === PHASE.PAYMENT) {
      const hasValidAmount = amount && parseFloat(amount) > 0;
      return (
        <div className="cs-pa-step-content">
          <p className="cs-pa-question">התשלום טופל?</p>
          <div className="cs-pa-payment-row">
            <span className="cs-pa-currency">₪</span>
            <input
              type="number"
              inputMode="decimal"
              className="cs-pa-input"
              placeholder="סכום"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              aria-label="סכום לתשלום (דוגמה)"
            />
          </div>
          <div className="cs-pa-action-btns">
            <button
              type="button"
              className="cs-pa-btn cs-pa-btn-confirm"
              disabled={!hasValidAmount}
              onClick={() => setHint(offScript.paymentYes)}
              aria-label="שולם"
            >
              <Check size={16} weight="bold" />
            </button>
            <button
              type="button"
              className="cs-pa-btn cs-pa-btn-cancel pad-pulse"
              onClick={() => advance(PHASE.PAYMENT_TASK)}
              aria-label="לא שולם"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
          {renderHint()}
        </div>
      );
    }

    if (stageKey === "payment" && phase === PHASE.PAYMENT_TASK) {
      return (
        <div className="cs-pa-step-content">
          <p className="cs-pa-question">להוסיף תזכורת גבייה?</p>
          <div className="cs-pa-action-btns">
            <button
              type="button"
              className="cs-pa-btn cs-pa-btn-confirm pad-pulse"
              onClick={() => advance(PHASE.DONE)}
              aria-label="כן, להוסיף תזכורת"
            >
              <Check size={16} weight="bold" />
            </button>
            <button
              type="button"
              className="cs-pa-btn cs-pa-btn-cancel"
              onClick={() => setHint(offScript.taskNo)}
              aria-label="לא, תודה"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
          {renderHint()}
        </div>
      );
    }

    return null;
  };

  const renderStagePanel = (stage) => {
    const { key, Icon, title } = stage;
    if (done[key]) {
      return (
        <div key={key} className="cs-pa-stage cs-pa-stage-done" data-stage={key}>
          <div className="cs-pa-stage-header">
            <div className="cs-pa-stage-title">
              <Icon size={15} weight="fill" />
              <span>{title}</span>
            </div>
            <span className="cs-pa-stage-badge done">
              <Check size={10} weight="bold" />
              הושלם
            </span>
          </div>
        </div>
      );
    }
    const body = renderStageBody(key);
    return (
      <div key={key} className="cs-pa-stage cs-pa-stage-pending" data-stage={key}>
        <div className="cs-pa-stage-header">
          <div className="cs-pa-stage-title">
            <Icon size={15} weight="fill" />
            <span>{title}</span>
          </div>
          <span className="cs-pa-stage-badge pending">נדרש עדכון</span>
        </div>
        {body && (
          <div className="cs-pa-stage-body" key={phase}>
            {body}
          </div>
        )}
      </div>
    );
  };

  const renderDone = () => (
    <div className="pad-done" key={`done-${runKey}`}>
      <div className="pad-done-icon">
        <Wine size={30} weight="duotone" />
      </div>
      <h4 className="pad-done-title">זהו. שלושה עדכונים, אפס דברים שנשכחו.</h4>
      <p className="pad-done-text">
        ככה זה נראה גם ביום אמיתי: בסוף היום, כוס יין ביד, עוברים על מה שמחכה
        — פגישה אחרי פגישה — {g("ויודע", "ויודעת")} ששום דבר לא הלך לאיבוד.
        לא סיכום, לא תשלום, לא תזכורת.
      </p>
      <div className="pad-done-recap">
        <span className="pad-done-recap-item"><CheckCircle size={14} weight="fill" /> הפגישה אושרה</span>
        <span className="pad-done-recap-item"><CheckCircle size={14} weight="fill" /> הסיכום תועד</span>
        <span className="pad-done-recap-item"><CheckCircle size={14} weight="fill" /> נוצרה תזכורת גבייה</span>
      </div>
      <p className="pad-done-note">
        הפגישה לדוגמה נעלמת עכשיו — כמו שפגישה אמיתית נעלמת מהרשימה אחרי שעדכנת
        הכל. שום דבר לא נשמר.
      </p>
      <div className="pad-done-actions">
        <button type="button" className="pad-btn pad-btn-primary" onClick={() => onClose(true)}>
          <Check size={16} weight="bold" />
          סיימתי, הבנתי
        </button>
        <button type="button" className="pad-btn pad-btn-ghost" onClick={restart}>
          <ArrowCounterClockwise size={15} weight="bold" />
          עוד פעם
        </button>
      </div>
    </div>
  );

  return (
    <div className="pad-root" ref={rootRef} key={runKey}>
      {/* Coach bubble (Lenny) */}
      {coach && (
        <div className="pad-coach" role="note">
          <img src={lennyIcon} alt="" className="pad-coach-avatar" />
          <div className="pad-coach-body">
            <div className="pad-coach-head">
              <span className="pad-coach-name">לני</span>
              <span className="pad-coach-title">{coach.title}</span>
            </div>
            <p className="pad-coach-text">{coach.text}</p>
            {coach.action && (
              <button type="button" className="pad-btn pad-btn-primary pad-coach-action" onClick={coach.action.onClick}>
                {coach.action.label}
              </button>
            )}
          </div>
          <button
            type="button"
            className="pad-coach-close"
            onClick={() => onClose(false)}
            aria-label="יציאה מההדגמה"
            title="יציאה מההדגמה"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      )}

      {/* The sample item — same skeleton as a real pending item */}
      <div
        className={`cs-pa-item pad-item ${isExpanded ? "expanded" : ""} ${
          pendingCount > 0 ? "has-pending" : ""
        }`}
      >
        <button
          type="button"
          className={`cs-pa-item-header ${phase === PHASE.INTRO ? "pad-pulse-soft" : ""}`}
          onClick={() => phase === PHASE.INTRO && setPhase(PHASE.APPROVAL)}
          aria-expanded={isExpanded}
        >
          <div className="cs-pa-item-info">
            <span className="cs-pa-item-name">
              {apt.patientName}
              <span className="pad-badge">דוגמה</span>
            </span>
            <span className="cs-pa-item-time">
              {formatDate(apt.startTime)} • {formatTime(apt.startTime)}
            </span>
          </div>
          <div className="cs-pa-item-meta">
            <div className="cs-pa-chips">
              {STAGES.map((s) => {
                const StageIcon = s.Icon;
                const isDone = done[s.key];
                return (
                  <span
                    key={s.key}
                    className={`cs-pa-chip ${isDone ? "done" : "pending"}`}
                    data-stage={s.key}
                    title={isDone ? `${s.title} — הושלם` : `${s.title} — נדרש`}
                  >
                    {isDone ? <Check size={10} weight="bold" /> : <StageIcon size={10} weight="fill" />}
                    <span className="cs-pa-chip-label">{s.shortTitle}</span>
                  </span>
                );
              })}
            </div>
            <span className={`cs-pa-item-chevron ${isExpanded ? "rotated" : ""}`} aria-hidden="true">
              <CaretDown size={13} weight="bold" />
            </span>
          </div>
        </button>

        {isExpanded && (
          <div className="cs-pa-item-body">
            {phase === PHASE.DONE ? renderDone() : STAGES.map(renderStagePanel)}
          </div>
        )}
      </div>
    </div>
  );
}
